import { useCallback, useEffect, useRef } from 'react';
import { IS_PROD } from '@/app/constants';
import { addAwsWafToken, handleAwsWafError, handleAwsWafResponse } from '@/app/utils/client';

interface RequestOptions<TParams> extends Omit<RequestInit, 'body'> {
  baseURL?: string;
  credentials?: RequestInit['credentials'];
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'HEAD' | 'OPTIONS' | 'CONNECT' | 'TRACE';

  // 重连配置选项
  reconnectInterval?: number;
  heartbeatTimeout?: number;
  maxReconnectAttempts?: number;
  /** 流正常关闭（done=true）后是否自动重连，用于需要永久保持的 SSE 订阅 */
  reconnectOnClose?: boolean;

  onMessage?: (data: string, params: TParams) => void;
  onClose?: (params?: TParams) => void;
  onError?: (error: Error) => void;
}

export function useFetchStream<TParams extends Record<string, unknown>>(
  url: string,
  {
    method = 'GET',
    baseURL = '',
    credentials = 'include',
    reconnectInterval = 3000,
    heartbeatTimeout = 60000,
    maxReconnectAttempts = 5,
    reconnectOnClose = false,
    onMessage,
    onClose,
    onError,
    ...extraProps
  }: RequestOptions<TParams>,
) {
  const abortControllerRef = useRef<AbortController | null>(null);
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastMsgTimeRef = useRef<number>(0);

  // 重连次数
  const reconnectAttemptsRef = useRef(0);
  // 当前参数
  const currentParamsRef = useRef<TParams | undefined>(undefined);
  // 页面可见性状态
  const isPageVisibleRef = useRef(true);
  // 组件挂载状态
  const isMountedRef = useRef(false);

  useEffect(() => {
    lastMsgTimeRef.current = Date.now();
  }, []);

  // 清理资源包括心跳检测、重连、读取器、abortController
  const cleanup = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }

    readerRef.current?.cancel().catch(() => {});
    readerRef.current?.releaseLock?.();
    readerRef.current = null;

    // 解决在请求已经被 abort 后，还会重复调用的问题
    if (abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
      abortControllerRef.current?.abort();
    }
    abortControllerRef.current = null;
  }, []);

  // 重连逻辑
  const attemptReconnect = useCallback(
    (params: TParams) => {
      // 如果页面不可见，不进行重连
      if (!isPageVisibleRef.current) {
        console.warn('页面不可见，暂停重连');
        return;
      }

      if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
        console.warn('达到最大重连次数，停止重连');
        return;
      }

      reconnectAttemptsRef.current++;
      console.warn(`尝试重连 (${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);

      reconnectTimerRef.current = setTimeout(() => {
        fetchData(params, true); // 标记为重试
      }, reconnectInterval);
    },
    [reconnectInterval, maxReconnectAttempts],
  );

  // 处理错误，包括验证码错误
  const handleError = useCallback(
    async (error: Error) => {
      cleanup();
      const handled = await handleAwsWafError(error);

      if (currentParamsRef.current) {
        attemptReconnect(currentParamsRef.current);
      }

      if (!handled) {
        onError?.(error);
      }
    },
    [attemptReconnect, cleanup, onError],
  );

  // 心跳检测
  const startHeartbeat = useCallback(() => {
    const heartbeatInterval = Math.max(heartbeatTimeout / 3, 10000);

    heartbeatTimerRef.current = setInterval(() => {
      // 如果页面不可见，不进行心跳检测
      if (!isPageVisibleRef.current) {
        return;
      }

      const now = Date.now();
      if (now - lastMsgTimeRef.current > heartbeatTimeout && currentParamsRef.current) {
        console.warn('SSE 假死，尝试重连');
        attemptReconnect(currentParamsRef.current);
      }
    }, heartbeatInterval);
  }, []);

  // 封装 fetch 请求
  const fetchData = useCallback(
    async (params: TParams, isRetry = false) => {
      // 如果组件已经卸载，不再进行请求
      if (!isMountedRef.current) {
        return;
      }

      // 先创建新的 abortController，再清理旧连接
      // 这样可以确保在清理时不会影响到即将建立的新连接
      const abortController = new AbortController();

      // 清理之前的连接
      cleanup();

      // 设置新的 abortController
      abortControllerRef.current = abortController;
      currentParamsRef.current = params;

      // 只有在非重试时才重置重连计数器
      if (!isRetry) {
        reconnectAttemptsRef.current = 0;
      }
      lastMsgTimeRef.current = Date.now();

      let absoluteURL = url.startsWith('/') ? baseURL + url : url;
      let requestBody: string | undefined;

      if (method === 'GET') {
        if (params && typeof params === 'object') {
          // 过滤 undefined/null：encodeURIComponent(undefined) 会得到字符串 "undefined"，
          // 会把无意义的占位值拼到 query 上（例如 last_stream_seq=undefined），后端直接 400。
          const search = Object.entries(params)
            .filter(([, value]) => value !== undefined && value !== null)
            .map(([key, value]) => {
              if (Array.isArray(value)) {
                return value
                  .filter((v) => v !== undefined && v !== null)
                  .map((v) => `${key}=${encodeURIComponent(v)}`)
                  .join('&');
              }
              return `${key}=${encodeURIComponent(value as string)}`;
            })
            .filter(Boolean)
            .join('&');

          if (search) {
            absoluteURL += `?${search}`;
          }
        }
      } else if (method === 'POST') {
        requestBody = JSON.stringify(params);
      }

      const headers = new Headers(extraProps.headers);

      if (IS_PROD) {
        const backendCanary = document.cookie
          .split('; ')
          .find((row) => row.startsWith('backend_canary='))
          ?.split('=')[1];

        if (backendCanary === 'true') {
          headers.set('X-Backend-Canary', 'true');
        }
      }

      // 使用统一的 AWS WAF token 处理函数
      await addAwsWafToken(headers, absoluteURL);

      // 在异步操作后检查 abortController 是否仍然有效
      // 如果已经被替换，说明组件已经重新挂载或卸载，应该中止这次请求
      if (abortControllerRef.current !== abortController) {
        return;
      }

      // 如果 abortController 已经被 abort，直接返回，避免发起无效请求
      if (abortController.signal.aborted) {
        return;
      }

      try {
        const response = await fetch(absoluteURL, {
          ...extraProps,
          method,
          headers,
          credentials,
          body: requestBody,
          signal: abortControllerRef.current.signal, // 使用新创建的 controller 的 signal
        });

        if (!response.ok) {
          // 使用统一的 AWS WAF 响应处理函数
          handleAwsWafResponse(response, absoluteURL);

          // 如果不是 WAF 错误，则尝试解析错误数据
          const errorData = await response.json();
          throw errorData;
        }

        const reader = response.body?.getReader();
        if (reader) {
          readerRef.current = reader;
          lastMsgTimeRef.current = Date.now();

          // 启动心跳检测
          startHeartbeat();

          const decoder = new TextDecoder('utf-8');

          const readLoop = async () => {
            try {
              while (true) {
                const { done, value } = await reader.read();

                if (done) {
                  cleanup();
                  if (reconnectOnClose && isMountedRef.current) {
                    attemptReconnect(params);
                  } else {
                    reconnectAttemptsRef.current = 0;
                    currentParamsRef.current = undefined;
                    onClose?.({ ...params });
                  }
                  return;
                }

                lastMsgTimeRef.current = Date.now();
                const data = decoder.decode(value);

                if (data.trim()) {
                  onMessage?.(data, params);
                }
              }
            } catch (error) {
              // 对于 SSE 来说，当 AbortError 时，在 reader.read() 时会抛出错误
              if (error instanceof Error && error.name === 'AbortError') {
                reader.releaseLock();
                return;
              }

              console.error('readLoop error', error);
              throw error;
            }
          };

          readLoop().catch((error) => {
            // 外层手动断开链接时，会报AbortError，此时不返回onError
            if (error instanceof Error && error.name === 'AbortError') {
              return;
            }

            handleError(error as Error);
          });
        }
      } catch (error) {
        // 外层手动断开链接时，会报AbortError，此时不返回onError
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }

        handleError(error as Error);
      }
    },
    [url],
  );

  // 页面可见性监听
  useEffect(() => {
    isMountedRef.current = true;

    const handler = () => {
      if (document.visibilityState === 'visible') {
        // 页面重新可见
        isPageVisibleRef.current = true;

        if (currentParamsRef.current) {
          const hasActiveReader = !!readerRef.current;
          const now = Date.now();
          const isHeartbeatTimeout = now - lastMsgTimeRef.current > heartbeatTimeout;

          // 页面恢复可见时，如果连接已断开或心跳超时，立即重连。
          if (!hasActiveReader || isHeartbeatTimeout) {
            console.warn('页面重新可见，重置重连计数器并重新连接');
            fetchData(currentParamsRef.current, false);
          }
        }
      } else {
        // 页面隐藏
        isPageVisibleRef.current = false;
        console.warn('页面隐藏，暂停重连逻辑');
      }
    };

    document.addEventListener('visibilitychange', handler);
    return () => {
      isMountedRef.current = false;
      document.removeEventListener('visibilitychange', handler);
      cleanup();
    };
  }, []);

  // 关闭请求
  const close = useCallback(() => {
    cleanup();
    currentParamsRef.current = undefined; // 清空当前参数，防止页面可见性监听重连
    reconnectAttemptsRef.current = 0; // 重置重连计数器
  }, [cleanup]);

  // 运行请求
  const run = useCallback(
    (params: TParams) => {
      fetchData(params, false); // 非重试，正常连接
    },
    [fetchData],
  );

  return {
    run,
    close,
  };
}
